import { Component, computed, effect, input, output, inject } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatDatepickerInputEvent, MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { DateOnly } from '../../../../shared/models/date-only';
import { CropYearService } from '../../../../shared/services/crop-year.service';
import { BemAIIdentifiers } from '../../../shared/constants';
import {
  AvailableBemCropVariety,
  ChangeCropVarietyRequest,
  ChangePlantDateRequest,
  CropFieldBemDto,
  CropFieldNutrientCalculations,
  GetCropFieldBemResponse,
} from '../../../shared/models/models';
import { MatCard, MatCardContent } from '@angular/material/card';
import { CropfieldSelectorComponent } from '../cropfield-selector/cropfield-selector.component';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatTooltip } from '@angular/material/tooltip';
import { NgClass } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { NotificationEmptyComponent } from '@next/core-lib/components';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@next/core-lib/i18n';
import {
  PlantDateWarningComponent,
} from 'src/app/shared/components/plant-date-warning.component/plant-date-warning.component';
import {
  getPlantDateLimits,
  getPlantDateWarningCodes,
} from 'src/app/shared/components/plant-date-warning.component/plant-date-warning.helpers';
import { DateService } from 'src/app/shared/services/date.service';
import { toNativeDate } from 'src/app/shared/utils';
import { CropYearSelectorComponent } from '../../../../shared/components/crop-year-selector/crop-year-selector.component';

@Component({
  selector: 'app-crop-field-info',
  templateUrl: './crop-field-info.component.html',
  styleUrls: ['./crop-field-info.component.scss'],
  imports: [
    MatSelect,
    MatOption,
    MatCard,
    MatCardContent,
    CropfieldSelectorComponent,
    MatFormField,
    MatTooltip,
    NgClass,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    ReactiveFormsModule,
    NotificationEmptyComponent,
    MatProgressSpinner,
    TranslatePipe,
    PlantDateWarningComponent,
    CropYearSelectorComponent,
  ],
})
export class CropFieldInfoComponent {
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);
  private readonly dateService = inject(DateService);
  private readonly cropYearService = inject(CropYearService);
  readonly isCropYearReadOnly = this.cropYearService.isReadOnly;
  protected readonly plantDateFormControl = new FormControl<Date | null>(null);
  protected readonly plantDateLimits = getPlantDateLimits(this.dateService);

  nutrientCalculations = input<CropFieldNutrientCalculations[]>([]);

  bemDataResponse = input<GetCropFieldBemResponse | null>();

  availableVarieties = input<AvailableBemCropVariety[]>([]);
  varietiesLoadFailed = input(false);
  isSaving = input(false);
  readonly varietyControl = new FormControl<number | null>(null);
  readonly currentVarietyId = computed(() => this.bemData()?.cropVariety?.id ?? this.bemData()?.cropField.cropVarietyId ?? null);
  readonly currentVarietyIsAvailable = computed(() => this.availableVarieties().some((variety) => variety.id === this.currentVarietyId()));
  readonly isVarietyReadonly = computed(() => this.isPlantDateConfirmed() || this.isCropYearReadOnly());
  readonly isVarietyDisabled = computed(() => this.isVarietyReadonly() || this.isSaving());

  readonly cropVarietyChange = output<ChangeCropVarietyRequest>();

  isLoading = computed(() => {
    return this.bemDataResponse() === null;
  });

  error = computed(() => {
    const data = this.bemDataResponse();
    if (data !== null && data !== undefined && !this.isSuccessResponse(data)) {
      return data;
    }
    return null;
  });

  bemData = computed(() => {
    const data = this.bemDataResponse();
    if (data && this.isSuccessResponse(data)) {
      return data;
    }
  });

  cropfieldId = computed(() => {
    return this.bemData()?.cropField.id;
  });

  areSampleDatesConfirmed = computed(() => {
    return this.bemData()?.areSampleDatesConfirmed ?? false;
  });

  isPlantDateConfirmed = computed(() => {
    return this.bemData()?.cropField.isPlantDateConfirmed ?? false;
  });

  readonly isPlantDateReadonly = computed(() => this.isPlantDateConfirmed() || this.isCropYearReadOnly() || this.isSaving());

  matTooltipText = computed(() => {
    if (this.isCropYearReadOnly()) return 'UI.CurrentYearOnlyMutationMessage';
    if (this.isPlantDateConfirmed()) return 'UI.PlantDateConfirmedMessage';
    return '';
  });

  displayedPlantDate = computed(() => {
    const areSampleDatesConfirmed = this.areSampleDatesConfirmed();
    const bemData = this.bemData();

    return areSampleDatesConfirmed && bemData?.cropField.plantDate
      ? bemData.cropField.plantDate
      : null;
  });

  plantDateWarningCodes = computed(() => {
    return getPlantDateWarningCodes({
      plantDate: this.bemData()?.cropField.plantDate,
      plantDateCreatedAt: this.bemData()?.cropField.plantDateCreatedAt,
      plantDateUpdatedAt: this.bemData()?.cropField.plantDateUpdatedAt,
      isParticipatingToBem: true,
    });
  });

  plantDateChange = output<ChangePlantDateRequest>();
  selectedCropFieldIdChange = output<number>();

  constructor() {
    effect(() => {
      this.bemDataResponse();
      this.varietyControl.setValue(this.currentVarietyId(), { emitEvent: false });
    });
    effect(() => {
      if (this.isVarietyDisabled()) {
        this.varietyControl.disable({ emitEvent: false });
      } else {
        this.varietyControl.enable({ emitEvent: false });
      }
    });
    effect(() => {
      this.bemDataResponse();
      this.plantDateFormControl.setValue(this.displayedPlantDate(), { emitEvent: false });

      if (this.isPlantDateReadonly()) {
        this.plantDateFormControl.disable({ emitEvent: false });
      } else {
        this.plantDateFormControl.enable({ emitEvent: false });
      }
    });
  }

  isSuccessResponse(response: GetCropFieldBemResponse | null | undefined): response is CropFieldBemDto {
    return !!response && 'cropField' in response;
  }

  onVarietyChange(cropVarietyId: number): void {
    const cropFieldId = this.cropfieldId();
    if (!cropFieldId || this.isVarietyDisabled() || this.varietiesLoadFailed() || cropVarietyId === this.currentVarietyId() ||
      !this.availableVarieties().some((variety) => variety.id === cropVarietyId)) return;
    this.cropVarietyChange.emit({ cropFieldId, cropVarietyId });
  }

  onCropFieldChange(cropFieldId: number): void {
    this.ai.trackEvent(BemAIIdentifiers.changeCropField, {});
    this.selectedCropFieldIdChange.emit(cropFieldId);
  }

  handleSubmitPlantDate(event: MatDatepickerInputEvent<Date>): void {
    if (this.isPlantDateReadonly()) {
      return;
    }

    const selectedDate = toNativeDate(event.value);
    const cropFieldId = this.cropfieldId();

    if (selectedDate && cropFieldId && this.plantDateFormControl.valid) {
      this.ai.trackEvent(BemAIIdentifiers.editPlantingDate, {});
      this.plantDateChange.emit({
        cropFieldId: cropFieldId,
        customerPlantDate: DateOnly.fromDate(selectedDate),
      });
    }
  }
}
